<template>
    <div class="offer-presets-toolbar">
        <v-text-field
            v-model="search"
            class="offer-presets-toolbar__search"
            :placeholder="toSentenceCase($t('admin.offerPresets.search'))"
            :aria-label="$t('admin.offerPresets.search')"
            prepend-inner-icon="mdi-magnify"
            density="compact"
            variant="outlined"
            clearable
            hide-details
        />
        <v-select
            v-model="source"
            class="offer-presets-toolbar__filter"
            :items="sourceOptions"
            :aria-label="$t('admin.offerPresets.source')"
            density="compact"
            variant="outlined"
            hide-details
        />
        <v-select
            v-model="tag"
            class="offer-presets-toolbar__filter"
            :items="tagOptions"
            item-title="label"
            item-value="id"
            :aria-label="$t('admin.offerPresets.priceTag')"
            density="compact"
            variant="outlined"
            hide-details
        />
        <v-spacer />
        <v-menu>
            <template #activator="{ props: menuProps }">
                <v-btn v-bind="menuProps" color="primary" prepend-icon="mdi-plus">
                    {{ toSentenceCase($t('admin.offerPresets.newPreset')) }}
                </v-btn>
            </template>
            <v-list>
                <v-list-item
                    v-for="creationSource in creationSources"
                    :key="creationSource.id"
                    :title="toSentenceCase($t(creationSource.labelKey))"
                    @click="emit('create', creationSource.id)"
                />
            </v-list>
        </v-menu>
    </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import i18n from '@/js/vue-i18n';
import { toSentenceCase } from '@/js/utils/string-utils';
import { getPriceTagLabel } from '@/js/utils/offer-template-label';

const props = defineProps({
    priceTags: { type: Array, default: () => [] },
    creationSources: { type: Array, default: () => [] },
});
const emit = defineEmits(['filter', 'create']);
const search = ref('');
const source = ref('all');
const tag = ref('all');
const sourceOptions = computed(() => [
    { title: toSentenceCase(i18n.global.t('admin.offerPresets.allSources')), value: 'all' },
    { title: toSentenceCase(i18n.global.t('admin.offerPresets.system')), value: 'system' },
    { title: toSentenceCase(i18n.global.t('admin.offerPresets.custom')), value: 'custom' },
]);
const tagOptions = computed(() => [
    { id: 'all', label: toSentenceCase(i18n.global.t('admin.offerPresets.allTags')) },
    ...props.priceTags.map(item => ({
        id: item.id,
        label: getPriceTagLabel({ id: item.id, name: item.name }),
    })),
]);
watch([search, source, tag], () =>
    emit('filter', { search: search.value, source: source.value, tag: tag.value })
);
</script>

<style scoped lang="scss">
.offer-presets-toolbar {
    display: flex;
    flex: 0 0 auto;
    flex-wrap: wrap;
    align-items: center;
    gap: 1.2rem;

    &__search {
        flex: 0 1 32rem;
    }

    &__filter {
        flex: 0 1 22rem;
    }

    :deep(.v-field),
    :deep(.v-btn) {
        font-size: 1.4rem;
        letter-spacing: normal;
        text-transform: none;
    }

    // The legacy Vuetify 2 sheet sets `.v-text-field input { padding: 8px 0 }` and `.v-input { font-size: 1.2rem }`.
    :deep(.v-input) {
        font-size: 1.6rem;
    }

    :deep(.v-field--variant-outlined) {
        font-size: 1.6rem;

        input.v-field__input,
        div.v-field__input {
            min-height: 4.4rem;
            padding: 1rem 1.4rem;
            font-size: 1.6rem;
            line-height: 2.4rem;
        }

        .v-select__selection-text {
            font-size: 1.6rem;
        }

        .v-field__prepend-inner,
        .v-field__append-inner,
        .v-field__clearable {
            align-items: center;
            padding-top: 0;
        }

        .v-field__prepend-inner {
            padding-inline-start: 1.2rem;
        }
    }

    :deep(.v-btn) {
        padding-inline: 1.6rem;
    }
}
</style>
